<!-- Roaster (pushed, /tabs/shelf/roaster/:slug). See IDEATION.md "7. Roaster".

     Opens with that roaster's bay, small and static: the same ShelfWall the
     Shelf uses, fed one bay, non-interactive. The bay's height is chosen so the
     whole bay fits the width (a bay is 1–3 plates wide), capped so it never
     pushes the list off a phone. -->
<template>
  <IonPage>
    <!-- The name is printed once, as the display heading on the sheet below
         the bay; the bar carries only Back (SHELL.md: a screen names itself
         once). -->
    <AppHeader :title="roaster ? '' : 'Roaster'" back="/tabs/shelf" />

    <IonContent :fullscreen="true" class="roaster">
      <div v-if="!roaster" class="empty">
        <h2 class="f-display">Not on the wall</h2>
        <p>That roaster is no longer here. It may have left BeanHub's range since this link was made.</p>
        <button type="button" class="f-btn" @click="goBack">Back to the shelves</button>
      </div>

      <template v-else>
        <!-- The bay, as a picture. aria-hidden: the list below says the same
             thing in words, and every bag is also a row there. -->
        <div ref="bayBox" class="band" aria-hidden="true">
          <div class="bay" :style="{ height: `${bayH}px`, width: `${bayFitW}px` }">
            <ShelfWall
              v-if="bayW"
              ref="wall"
              :bays="[bay]"
              :interactive="false"
              :reduced-motion="reducedMotion"
            />
          </div>
        </div>

        <div class="head">
          <p class="f-kicker">{{ coffees.length }} {{ coffees.length === 1 ? 'coffee' : 'coffees' }}</p>
          <h1 class="f-display head__title">{{ roaster.name }}</h1>
          <p v-if="roaster.blurb" class="head__blurb">{{ roaster.blurb }}</p>
          <button type="button" class="f-btn f-btn--block head__btn" @click="showOnWall">
            Walk to their bay <i class="f-arrow" aria-hidden="true">→</i>
          </button>
        </div>

        <h2 class="list-title">Their coffees</h2>

        <ul v-if="coffees.length" class="rows" role="list">
          <li v-for="c in coffees" :key="c.slug">
            <RouterLink :to="`/tabs/shelf/coffee/${c.slug}`" class="row" :class="{ 'row--out': !c.inStock }">
              <span class="row__thumb"><BagFront :coffee="c" variant="thumb" class="row__front" /></span>
              <span class="row__text">
                <h3 class="row__name">{{ c.name }}</h3>
                <span class="row__notes">{{ (c.notes || []).slice(0, 3).join(' · ') || c.brew }}</span>
              </span>
              <span class="row__price f-mono">{{ c.inStock ? `from $${c.fromPrice}` : 'Sold out' }}</span>
            </RouterLink>
          </li>
        </ul>

        <div v-else class="empty">
          <p>No coffees from {{ roaster.name }} are in the range right now.</p>
        </div>
      </template>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch, nextTick } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { IonPage, IonContent, onIonViewDidEnter } from '@ionic/vue';
import { AppHeader } from '../core/ui';
import ShelfWall from '../components/ShelfWall.vue';
import BagFront from '../components/BagFront.vue';
import {
  roasterBySlug, roasterColour, coffees as allCoffees, grouping, planogramSort, reducedMotion,
} from '../store.js';

const props = defineProps({
  slug: { type: String, required: true },
});

const route = useRoute();
const router = useRouter();
const slug = computed(() => props.slug || route.params.slug);

const roaster = computed(() => roasterBySlug(slug.value));
const coffees = computed(() =>
  allCoffees.value
    .filter((c) => c.roasterSlug === slug.value)
    .sort(planogramSort));

// One bay, the shape store.bays produces, regardless of the Shelf's grouping.
const bay = computed(() => ({
  key: slug.value,
  title: roaster.value?.name || '',
  colour: roasterColour(slug.value),
  coffees: coffees.value,
  matchCount: coffees.value.length,
}));

// --- sizing the bay so the whole of it fits the width -------------------------
const PLATE_ASPECT = 768 / 1376; // w/h of a plate (ShelfWall's)
const bayBox = ref(null);
const wall = ref(null);
const bayW = ref(0);
const plates = ref(1);
const bayH = computed(() => {
  if (!bayW.value) return 260;
  const fit = bayW.value / (plates.value * PLATE_ASPECT);
  return Math.round(Math.max(220, Math.min(fit, 480)));
});
// The bay's own width at that height: on a wide screen it is centred on the
// paper rather than cropped, so the whole roaster is always on show.
const bayFitW = computed(() => Math.min(bayW.value || 390, Math.ceil(plates.value * PLATE_ASPECT * bayH.value)));

let ro = null;
function measure () {
  const el = bayBox.value; if (!el) return;
  bayW.value = el.clientWidth;
  nextTick(() => { const n = wall.value?.layout?.plates?.length; if (n) plates.value = n; });
}
watch(bayBox, (el) => {
  ro?.disconnect();
  if (!el) return;
  ro = new ResizeObserver(measure);
  ro.observe(el);
  measure();
});
watch(() => wall.value?.layout?.plates?.length, (n) => { if (n) plates.value = n; });
onIonViewDidEnter(measure);
onBeforeUnmount(() => ro?.disconnect());

function goBack () {
  router.replace('/tabs/shelf');
}

/** Rack by roaster and walk the Shelf to this bay (ShelfPage reads ?bay=). */
function showOnWall () {
  grouping.value = 'roaster';
  router.push({ path: '/tabs/shelf', query: { bay: slug.value } });
}
</script>

<style scoped>
.roaster { --background: var(--paper); }

.band {
  display: flex;
  justify-content: center;
  background: var(--paper);
  /* The photo runs under the translucent header; the head card overlaps it
     like the Shelf's dock does. */
  margin-bottom: calc(-1 * var(--radius-sheet));
}
.bay {
  position: relative;
  overflow: hidden;
  max-width: 100%;
}

.head {
  position: relative;
  padding: var(--sp-5) var(--gutter) var(--sp-5);
  background: var(--dock);
  border-radius: var(--radius-sheet) var(--radius-sheet) 0 0;
  box-shadow: var(--shadow-dock);
}
.head__title {
  margin: var(--sp-2) 0 0;
  font-size: var(--text-display);
  letter-spacing: var(--display-tracking-xl);
}
.head__blurb {
  margin: var(--sp-3) 0 0;
  color: var(--ink-2);
  line-height: 1.55;
  max-width: 38rem;
}
.head__btn { margin-top: var(--sp-5); max-width: 26rem; }

.list-title {
  margin: 0;
  padding: var(--sp-5) var(--gutter) var(--sp-2);
  font: 800 var(--text-lg) / 1.2 var(--font-display);
  letter-spacing: var(--display-tracking);
  color: var(--ink);
  background: var(--dock);
}

.rows {
  list-style: none;
  margin: 0;
  padding: 0 0 var(--sp-7);
  background: var(--dock);
}
.row {
  display: grid;
  grid-template-columns: 48px 1fr auto;
  align-items: center;
  gap: var(--sp-3);
  min-height: 72px;
  padding: var(--sp-2) var(--sp-3);
  margin: 0 calc(var(--gutter) - var(--sp-3));
  color: var(--ink);
  text-decoration: none;
  border-radius: var(--radius);
  transition: background-color var(--dur) var(--ease);
}
.row:hover { background: color-mix(in srgb, var(--sage) 55%, var(--dock)); }
.row:focus-visible { outline: 2px solid var(--mustard); outline-offset: -2px; }
.row__thumb { display: flex; align-items: flex-end; justify-content: center; height: 60px; }
.row__front { height: 60px; width: auto; max-width: 48px; }
.row__text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.row__name { margin: 0; font-size: inherit; font-weight: 700; line-height: 1.25; }
.row__notes { color: var(--muted); font-size: var(--text-sm); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.row__price { color: var(--ink); white-space: nowrap; }
.row--out .row__name, .row--out .row__price { color: var(--muted); }

.empty {
  padding: 3rem var(--gutter);
  text-align: center;
  color: var(--muted);
}
.empty h2 { font-size: var(--text-2xl); margin: 0 0 var(--sp-2); }
.empty p { margin: 0 0 var(--sp-5); }

@media (min-width: 900px) {
  /* One 46rem reading column, centred under the bay. */
  .head, .list-title, .rows { padding-inline: max(var(--gutter), calc((100% - 46rem) / 2)); }
}
</style>
